import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useI18nStore } from '@/store/pinia/i18n'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction, makeStartFetchAction, makeStopFetchAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

export const state = () => ({
  profile: null,
  errors: {
    profile: [],
    resetPassword: [],
    orgCreationNotAllowedMessage: [],
  },
  fetchInProgress: {
    orgCreationNotAllowedMessage: false,
  },
  orgCreationNotAllowedMessage: null,
  guide: {
    presentationCompleted: false,
    tour: {
      currentStep: 0,
      completed: true,
    },
    onboardingChecklistHidden: false,
  },
})

export const useUserStore = defineStore('user', {
  state,

  getters: {
    email: (state) => _.$get(state.profile, 'emails', []).find(({ purpose }) => purpose === 'primary')?.email || null,
    isCycloidEmployee: (state) => /^[A-Za-z0-9._%+-]+@cycloid\.io$/.test(_.find(state.profile?.emails, ['purpose', 'primary'])?.email),
    isTourInProgress: (state) => !state.guide.tour.completed && state.guide.tour.currentStep > 0,
  },

  actions: {
    CLEAR_USER_ERRORS: makeClearErrorsAction(state),
    RESET_USER_STATE: makeResetStoreAction(state),
    SET_USER_ERRORS: makeSetErrorsAction(),
    START_FETCH: makeStartFetchAction(),
    STOP_FETCH: makeStopFetchAction(),

    async GET_ORG_CREATION_NOT_ALLOWED ({ orgCanonical }) {
      this.START_FETCH('orgCreationNotAllowedMessage')
      this.CLEAR_USER_ERRORS('orgCreationNotAllowedMessage')
      if (_.$isEmpty(orgCanonical)) {
        this.STOP_FETCH('orgCreationNotAllowedMessage')
        return
      }
      const raw = await this.$cycloid.ydAPI.getOrganizationCreationNotAllowed(orgCanonical)
      if (raw === null) {
        this.SET_ORG_CREATION_NOT_ALLOWED(null)
      } else {
        const { data, errors } = raw
        if (data) {
          this.SET_ORG_CREATION_NOT_ALLOWED(data.creation_restricted_message ?? data.message ?? null)
        } else {
          this.SET_ORG_CREATION_NOT_ALLOWED(null)
        }
        if (!_.isEmpty(errors)) {
          const isMissingMessageOnly = _.isEmpty(data) && _.some(errors, (e) => {
            const code = e?.code
            return code === 'not_found' || code === 'NotFound'
          })
          if (!isMissingMessageOnly) {
            this.SET_USER_ERRORS({ key: 'orgCreationNotAllowedMessage', errors })
          }
        }
      }
      this.STOP_FETCH('orgCreationNotAllowedMessage')
    },

    async UPDATE_ORG_CREATION_NOT_ALLOWED ({ orgCanonical, message }) {
      const alertsStore = useAlertsStore()

      this.CLEAR_USER_ERRORS('orgCreationNotAllowedMessage')
      if (_.$isEmpty(orgCanonical)) return false
      const { errors } = (await this.$cycloid.ydAPI.updateOrganizationCreationNotAllowed(orgCanonical, message)) || {}
      if (!_.isEmpty(errors)) {
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_USER_ERRORS({ key: 'orgCreationNotAllowedMessage', errors })
      } else {
        this.SET_ORG_CREATION_NOT_ALLOWED(message)
        alertsStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.orgCreationNotAllowedMessage.updated') })
      }
      return _.isEmpty(errors)
    },

    SET_ORG_CREATION_NOT_ALLOWED (message) {
      this.orgCreationNotAllowedMessage = message
    },

    async GET_PROFILE (jwtUser = {}) {
      const i18nStore = useI18nStore()
      const alertsStore = useAlertsStore()
      const { data, errors } = (await this.$cycloid.ydAPI.getUserAccount()) || {}
      if (data) {
        const { guide, ...profile } = data
        this.SET_PROFILE({ ...jwtUser, ...profile })
        i18nStore.CHANGE_LOCALE(profile.locale)
        if (!this.isTourInProgress) this.SET_GUIDE(guide)
      }
      if (errors) {
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
      }
    },

    async UPDATE_PROFILE ({ profile, i18nProp = 'updated' }) {
      this.CLEAR_USER_ERRORS('profile')
      const { data = {}, errors } = (await this.$cycloid.ydAPI.updateUserAccount(profile)) || {}
      const { guide, ...returnedProfile } = data
      if (!_.isEmpty(returnedProfile)) {
        const { useCustomersStore } = await import('@/store/pinia/customers')
        const customersStore = useCustomersStore()
        const organizationCanonical = _.$get(customersStore.scopes, '[0].canonical', customersStore.orgCanonical)
        const query = {
          ...(organizationCanonical ? { organization_canonical: organizationCanonical } : {}),
          ...(_.get(customersStore.scopes, '[1].canonical') ? { child_canonical: _.get(customersStore.scopes, '[1].canonical') } : {}),
        }
        const { useAuthStore } = await import('@/store/pinia/auth')
        const authStore = useAuthStore()
        await authStore.REFRESH_TOKEN({ query })
        this.SET_PROFILE({ ...profile, ...returnedProfile })
        const { useAlertsStore } = await import('@/store/pinia/alerts')
        const alertsStore = useAlertsStore()
        alertsStore.SHOW_ALERT({ type: 'success', content: i18n.global.t(`alerts.success.profile.${i18nProp}`) })
      }
      if (errors) this.SET_USER_ERRORS({ key: 'profile', errors })
    },

    async DELETE_ACCOUNT ({ $router }) {
      const { errors } = (await this.$cycloid.ydAPI.deleteUserAccount()) || {}
      if (!_.isEmpty(errors)) {
        const { useAlertsStore } = await import('@/store/pinia/alerts')
        const alertsStore = useAlertsStore()
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
      } else {
        const { useAuthStore } = await import('@/store/pinia/auth')
        const authStore = useAuthStore()
        authStore.LOGOUT()
        $router.replace({ name: 'login', query: { action: 'accountDeleted', email: this.email } })
      }
    },

    async EMAIL_VERIFICATION_RESEND ({ email }) {
      const { errors } = (await this.$cycloid.ydAPI.resendVerifyEmail({ email })) || {}
      if (errors) return this.SET_USER_ERRORS({ key: 'emailVerificationResend', errors })

      const { useAlertsStore } = await import('@/store/pinia/alerts')
      const alertsStore = useAlertsStore()
      alertsStore.SHOW_ALERT({ type: 'success', content: i18n.global.t(`alerts.success.profile.email.verification.sent`) })
    },

    async RESET_PASSWORD ({ email }) {
      this.CLEAR_USER_ERRORS('resetPassword')

      const { errors } = (await this.$cycloid.ydAPI.passwordResetReq(email)) || {}
      if (errors) this.SET_USER_ERRORS({ key: 'resetPassword', errors })
    },

    async COMPLETE_PRESENTATION () {
      this.SET_PRESENTATION_COMPLETE()

      const { errors } = (await this.$cycloid.ydAPI.updateUserGuide(this.guide)) || {}
      if (errors) {
        const { useAlertsStore } = await import('@/store/pinia/alerts')
        const alertsStore = useAlertsStore()
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
      }
    },

    async COMPLETE_TOUR () {
      this.SET_TOUR_COMPLETE()

      const { errors } = (await this.$cycloid.ydAPI.updateUserGuide(this.guide)) || {}
      if (errors) {
        const { useAlertsStore } = await import('@/store/pinia/alerts')
        const alertsStore = useAlertsStore()
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
      }
    },

    async RESET_TOUR () {
      this.SET_TOUR_STEP(0)
      this.CLEAR_TOUR_COMPLETE()

      const { errors } = (await this.$cycloid.ydAPI.updateUserGuide(this.guide)) || {}
      if (errors) {
        const { useAlertsStore } = await import('@/store/pinia/alerts')
        const alertsStore = useAlertsStore()
        alertsStore.SHOW_ALERT({ type: 'error', content: errors })
      }
    },

    SET_PROFILE (profile) {
      const p = _.cloneDeep(profile)
      if (_.isEmpty(p.locale)) p.locale = i18n.locale
      if (_.has(p, 'country')) {
        p.country_code = p.country.code
        delete p.country
      }
      window.dataLayer?.push({ userLang: p.locale })
      this.$state['profile'] = _.cloneDeep(p)
      localStorage.setItem(LSK.PROFILE, JSON.stringify(p))
    },

    SET_GUIDE (guide) {
      delete guide.tutorial
      delete guide.stackcraftPresentationCompleted
      this.$state['guide'] = { ...this.guide, ...guide }
    },

    RESTORE_PROFILE () {
      if (!_.isEqual(this.profile, state().profile)) {
        window.dataLayer?.push({ userLang: this.profile.locale })
        return localStorage.setItem(LSK.PROFILE, JSON.stringify(this.profile))
      }
      const profile = localStorage.getItem(LSK.PROFILE)
      if (_.isEmpty(profile)) return
      this.$state['profile'] = JSON.parse(profile)
      window.dataLayer?.push({ userLang: this.profile.locale })
    },

    CLEAR_PROFILE () {
      window.dataLayer?.push({ userLang: null })
      this.$state['profile'] = _.cloneDeep(state().profile)
      localStorage.removeItem(LSK.PROFILE)
    },

    SET_PRESENTATION_COMPLETE () {
      this.$state.guide['presentationCompleted'] = true
    },

    CLEAR_PRESENTATION_COMPLETE () {
      this.$state.guide['presentationCompleted'] = false
    },

    SET_TOUR_STEP (step) {
      this.$state.guide.tour['currentStep'] = step
    },

    SET_TOUR_COMPLETE () {
      this.$state.guide.tour['completed'] = true
    },

    CLEAR_TOUR_COMPLETE () {
      this.$state.guide.tour['completed'] = false
    },
  },
})
